/**
 * `Y` — the occluder baseline (§4.3.1 `Layer.baseline_y`, §8.1 z-order rule).
 *
 * A walk-behind tree needs one number: the row where its trunk meets the
 * ground. A character whose feet are above that row draws behind it. This
 * tool drags exactly that line.
 */
import { idle, none, withState, type ToolHandler, type ToolResult } from './types'

export const layerTool: ToolHandler = {
  id: 'layer',
  shortcut: 'y',
  icon: 'Layers',
  label: 'Walk-behind',
  hint: 'Drag the dashed line to where the thing touches the ground.',
  cursor: 'ns-resize',

  reduce(machine, ev, cfg): ToolResult | null {
    const st = machine.state

    if (ev.t === 'pointerdown' && ev.button === 0) {
      const id = ev.hit.baseline ?? ev.hit.id ?? cfg.selection[0]
      if (!id) return idle('layer', [{ k: 'toast', message: 'Pick the shape that characters walk behind first.', tone: 'info' }])
      return withState('layer', { t: 'layer.baseline', id, y: ev.at[1] }, [
        { k: 'select', ids: [id] },
        { k: 'beginGesture' },
        { k: 'setBaseline', id, y: ev.at[1] },
      ])
    }

    if (ev.t === 'pointermove') {
      if (st.t !== 'layer.baseline') return none(machine)
      return withState('layer', { ...st, y: ev.at[1] }, [{ k: 'setBaseline', id: st.id, y: ev.at[1] }])
    }

    if (ev.t === 'pointerup') {
      if (st.t !== 'layer.baseline') return none(machine)
      return idle('layer', [{ k: 'endGesture' }])
    }

    if (ev.t === 'cancel' || ev.t === 'pointercancel') {
      return st.t === 'layer.baseline' ? idle('layer', [{ k: 'cancelGesture' }]) : idle('layer')
    }

    return null
  },
}
